import React from "react";
import {
  ColumnDef,
  functionalUpdate,
  OnChangeFn,
  VisibilityState,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { TableSortOptionDto, UpdateTableSettingsDto } from "@/client";
import { showActionErrorToast } from "@/state/error-toast";
import { useCurrentRef } from "@/hooks/use-current-ref";
import { useTableSettingsAdapter } from "./table-settings-adapter";
import {
  hasVisibleHideable,
  reconcileVisibility,
  specsOf,
  TableSettingsSnapshot,
  toTableSettingsPatch,
} from "./table-settings-layout";

/** Quiet time after the last change before it is written: one PATCH per burst of toggles. */
export const TABLE_SETTINGS_SAVE_DEBOUNCE_MS = 250;

export type TableSettingsState = {
  /** True when the table declared a `tableKey`, i.e. its layout is managed and persisted. */
  enabled: boolean;
  tableKey?: string;
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: OnChangeFn<VisibilityState>;
  /** Back to the declared defaults; the stored entry is removed server-side. */
  reset: () => Promise<void>;
  isResetting: boolean;
  /** Tells the hook the user changed the sort or the page size, so those are written too. */
  markInteraction: () => void;
};

export type UseTableSettingsStateArgs = {
  tableKey?: string;
  columns: ColumnDef<any, any>[];
  /** The table's current sort as the URL carries it (not an injected default). */
  sort: TableSortOptionDto[];
  pageSize?: number;
  /** Clears the table's sort (the URL) on reset. */
  onResetSort?: () => void;
};

// Same value by deep equality -> same reference, so effects keyed on it stay quiet for a fresh but
// identical array (the URL sort is re-parsed on every render).
const useDeepStable = <T>(value: T): T => {
  const ref = React.useRef(value);
  if (!_.isEqual(ref.current, value)) {
    ref.current = value;
  }
  return ref.current;
};

// "Nothing applied yet": distinct from an applied `undefined` (nothing stored for the table).
const UNAPPLIED = Symbol("unapplied");

/**
 * Column visibility as managed table state, with the user's saved layout applied and changes
 * written back to their profile (ID-6897). The rules:
 * - local state renders; the server never overrides a layout the user touched in this mount
 *   (an edit from another device shows up on the next mount);
 * - until the first interaction the saved layout is applied whenever it arrives or changes, so a
 *   slow /me still reconciles -- defaults until then, never a blank table;
 * - writes are debounced and carry only what changed since the last successful write; a failed
 *   write keeps the local state, toasts, and the next change retries it.
 * Without a `tableKey` the hook is plain local state, exactly as the table behaved before.
 */
export const useTableSettingsState = ({
  tableKey,
  columns,
  sort,
  pageSize,
  onResetSort,
}: UseTableSettingsStateArgs): TableSettingsState => {
  const enabled = !!tableKey;
  const { t } = useTranslation();
  const tRef = useCurrentRef(t);
  const adapter = useTableSettingsAdapter();
  const adapterRef = useCurrentRef(adapter);
  const entry = adapter.useEntry(enabled ? tableKey : undefined);

  const specs = useDeepStable(React.useMemo(() => specsOf(columns), [columns]));
  const stableSort = useDeepStable(sort);

  const [visibility, setVisibility] = React.useState<VisibilityState>(() =>
    enabled ? reconcileVisibility(specs, entry.value) : {},
  );
  const [isResetting, setIsResetting] = React.useState(false);

  const enabledRef = useCurrentRef(enabled);
  const tableKeyRef = useCurrentRef(tableKey);
  const specsRef = useCurrentRef(specs);
  const entryValueRef = useCurrentRef(entry.value);
  const sortRef = useCurrentRef(stableSort);
  const pageSizeRef = useCurrentRef(pageSize);
  const onResetSortRef = useCurrentRef(onResetSort);
  const interactedRef = React.useRef(false);
  // After a reset, until the URL has dropped the sort it carried (see reset()).
  const sortClearPendingRef = React.useRef(false);
  const appliedValueRef = React.useRef<unknown>(
    enabled ? entry.value : UNAPPLIED,
  );
  const userIdRef = React.useRef(entry.userId);

  const snapshot = React.useMemo<TableSettingsSnapshot>(
    () => ({
      ...toTableSettingsPatch(specs, visibility, entry.value),
      sort: stableSort,
      pageSize,
    }),
    [specs, visibility, entry.value, stableSort, pageSize],
  );
  const lastSavedRef = React.useRef<TableSettingsSnapshot>(snapshot);

  // Someone else signed in without a remount: start over from their layout. A cleared cache (an
  // account switch, /unresponsive) reports no user at all until /me is back: that is not a switch,
  // so the layout and the "touched" state stay as they are.
  React.useEffect(() => {
    if (entry.userId === undefined || userIdRef.current === undefined) {
      userIdRef.current = entry.userId ?? userIdRef.current;
      return;
    }
    if (entry.userId === userIdRef.current) {
      return;
    }
    userIdRef.current = entry.userId;
    interactedRef.current = false;
    appliedValueRef.current = UNAPPLIED;
  }, [entry.userId]);

  // Server -> local, only until the user touches the layout in this mount.
  React.useEffect(() => {
    if (!enabled || interactedRef.current) {
      return;
    }
    // No profile in the cache (not loaded yet, or just cleared): nothing to apply -- which is not
    // the same as "nothing stored".
    if (entry.userId === undefined) {
      return;
    }
    if (_.isEqual(entry.value, appliedValueRef.current)) {
      return;
    }
    appliedValueRef.current = entry.value;
    const next = reconcileVisibility(specsRef.current, entry.value);
    setVisibility(next);
    lastSavedRef.current = {
      ...toTableSettingsPatch(specsRef.current, next, entry.value),
      sort: sortRef.current,
      pageSize: pageSizeRef.current,
    };
  }, [enabled, entry.value, entry.userId]);

  // The column set changed (a permission-gated column appeared, the viewport flipped): keep what
  // the user had, let a column new to this layout take its declared default.
  const prevSpecsRef = React.useRef(specs);
  React.useEffect(() => {
    if (prevSpecsRef.current === specs) {
      return;
    }
    const prevSpecs = prevSpecsRef.current;
    prevSpecsRef.current = specs;
    if (!enabled) {
      return;
    }
    setVisibility((prev) =>
      reconcileVisibility(
        specs,
        toTableSettingsPatch(prevSpecs, prev, entryValueRef.current),
      ),
    );
  }, [specs, enabled]);

  // Writes run one after another so each one is diffed against what the server really holds.
  const chainRef = React.useRef<Promise<void>>(Promise.resolve());
  const runSave = React.useCallback((snap: TableSettingsSnapshot) => {
    const key = tableKeyRef.current;
    if (!key) {
      return;
    }
    chainRef.current = chainRef.current.then(async () => {
      const last = lastSavedRef.current;
      const body: UpdateTableSettingsDto = {};
      if (
        !_.isEqual(snap.visibleColumns, last.visibleColumns) ||
        !_.isEqual(snap.columnOrder, last.columnOrder)
      ) {
        // Always together: columnOrder is the "known columns" list a later-added column is
        // checked against, so it must describe the same layout as visibleColumns.
        body.visibleColumns = snap.visibleColumns;
        body.columnOrder = snap.columnOrder;
      }
      if (!_.isEqual(snap.sort, last.sort)) {
        body.sort = snap.sort; // [] clears the stored sort
      }
      if (snap.pageSize !== last.pageSize) {
        body.pageSize = snap.pageSize ?? null;
      }
      if (Object.keys(body).length === 0) {
        lastSavedRef.current = snap;
        return;
      }
      try {
        const saved = await adapterRef.current.save(key, body);
        lastSavedRef.current = snap;
        appliedValueRef.current = saved;
      } catch (error) {
        showActionErrorToast(error, tRef.current("tableSettingsSaveFailed"));
      }
    });
  }, []);

  const debouncedSave = React.useMemo(
    () => _.debounce(runSave, TABLE_SETTINGS_SAVE_DEBOUNCE_MS),
    [runSave],
  );
  // A toggle right before navigating away must still land.
  React.useEffect(
    () => () => {
      debouncedSave.flush();
    },
    [debouncedSave],
  );

  React.useEffect(() => {
    if (!enabled || !interactedRef.current) {
      return;
    }
    if (sortClearPendingRef.current) {
      if (snapshot.sort.length > 0) {
        return; // the URL has not caught up with the reset yet
      }
      sortClearPendingRef.current = false;
      lastSavedRef.current = { ...lastSavedRef.current, sort: [] };
    }
    if (_.isEqual(snapshot, lastSavedRef.current)) {
      // Toggled back within the quiet time: nothing to write after all.
      debouncedSave.cancel();
      return;
    }
    debouncedSave(snapshot);
  }, [enabled, snapshot, debouncedSave]);

  const onColumnVisibilityChange = React.useCallback<
    OnChangeFn<VisibilityState>
  >((updater) => {
    setVisibility((prev) => {
      const next = functionalUpdate(updater, prev);
      if (!enabledRef.current) {
        return next;
      }
      // The last visible column cannot be hidden; the panel disables it, this guards the state.
      if (!hasVisibleHideable(specsRef.current, next)) {
        return prev;
      }
      interactedRef.current = true;
      return next;
    });
  }, []);

  const markInteraction = React.useCallback(() => {
    if (enabledRef.current) {
      interactedRef.current = true;
    }
  }, []);

  const reset = React.useCallback(async () => {
    const key = tableKeyRef.current;
    if (!key) {
      return;
    }
    debouncedSave.cancel();
    setIsResetting(true);
    try {
      // A write still in flight must land before the delete, or it would re-create the entry.
      await chainRef.current;
      await adapterRef.current.reset(key);
      interactedRef.current = true;
      const next = reconcileVisibility(specsRef.current);
      appliedValueRef.current = undefined;
      // The URL keeps its page size (the route owns that default). The sort is cleared below, but
      // the URL catches up a render later: until it has, the current sort counts as "saved" so that
      // render schedules no write; once it has, the record becomes [] without a write -- the entry
      // is gone, there is nothing left to clear.
      lastSavedRef.current = {
        ...toTableSettingsPatch(specsRef.current, next),
        sort: sortRef.current,
        pageSize: pageSizeRef.current,
      };
      sortClearPendingRef.current = sortRef.current.length > 0;
      setVisibility(next);
      onResetSortRef.current?.();
    } catch (error) {
      showActionErrorToast(error, tRef.current("tableSettingsResetFailed"));
    } finally {
      setIsResetting(false);
    }
  }, [debouncedSave]);

  return React.useMemo<TableSettingsState>(
    () => ({
      enabled,
      tableKey,
      columnVisibility: visibility,
      onColumnVisibilityChange,
      reset,
      isResetting,
      markInteraction,
    }),
    [
      enabled,
      tableKey,
      visibility,
      onColumnVisibilityChange,
      reset,
      isResetting,
      markInteraction,
    ],
  );
};
